import { m } from 'framer-motion';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

// components
import { MotionViewport, varFade } from 'src/components/animate';
import Iconify from 'src/components/iconify';

// utils
import { features } from '../../utils/data';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  textAlign: 'center',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: theme.customShadows.z8,
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.customShadows.z24,
  },
}));

const StyledIconBox = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
  color: 'white',
}));

// ----------------------------------------------------------------------

export default function HomeFeatures() {
  const theme = useTheme();
  return (
    <Box
      sx={{
        py: { xs: 3, md: 6 },
        px: { xs: 2, md: 4 },
        bgcolor: 'background.default',
        width: '100%',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        minHeight: { xs: 'auto', md: '100vh' },
      }}
    >
      <Container maxWidth="xl" component={MotionViewport} sx={{ width: '100%' }}>
        <Stack spacing={2} sx={{ textAlign: 'center', mb: { xs: 3, md: 5 }, width: '100%' }}>
          <m.div variants={varFade().inDown}>
            <Typography variant="overline" sx={{ color: 'text.disabled', textAlign: 'center' }}>
              Why Choose Cartify
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="h2"
              sx={{
                my: 2,
                textAlign: 'center',
                fontSize: { xs: '1.75rem', sm: '2.125rem', md: '2.5rem' },
                px: { xs: 1, sm: 0 },
              }}
            >
              Powerful Features for
              <br />
              <Box
                component="span"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.warning.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Modern Businesses
              </Box>
            </Typography>
          </m.div>

          <m.div variants={varFade().inDown}>
            <Typography
              variant="body1"
              sx={{
                color: 'text.secondary',
                maxWidth: 600,
                mx: 'auto',
                textAlign: 'center',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 2, sm: 0 },
              }}
            >
              Our platform combines cutting-edge technology with industry expertise to deliver
              solutions that transform how you manage your supply chain.
            </Typography>
          </m.div>
        </Stack>

        <Grid
          container
          spacing={3}
          sx={{ justifyContent: 'center', width: '100%', px: { xs: 1, sm: 0 } }}
        >
          {features.map((feature, index) => (
            <Grid
              key={feature.title}
              item
              xs={12}
              sm={6}
              md={4}
              lg={3}
              sx={{ display: 'flex', justifyContent: 'center' }}
            >
              <m.div variants={varFade().inUp} style={{ width: '100%', maxWidth: '400px' }}>
                <StyledCard>
                  <StyledIconBox>
                    <Iconify icon={feature.icon} width={30} height={30} />
                  </StyledIconBox>
                  <CardContent sx={{ p: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{ mb: 1, fontWeight: 'bold', textAlign: 'center' }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        textAlign: 'center',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </CardContent>
                </StyledCard>
              </m.div>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
